/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                    FORM SUBMISSION - BACKEND INTEGRATION                   ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  This is the ONLY file you need to edit to connect forms to your backend  ║
 * ║                                                                           ║
 * ║  See BACKEND_INTEGRATION.md for detailed setup instructions               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Wired to the JuryView API (REACT_APP_API_URL). Every lead form on the
 * site funnels through submitLead(), which is why the reCAPTCHA token is
 * minted here rather than in each page — one choke point, no form can
 * forget to do it.
 *
 * Pair it with useLeadFormGuard() + <HoneypotField /> in the form
 * component to supply the honeypot and timing signals.
 */

import { executeRecaptcha } from './recaptcha';

/**
 * Action name this token is minted under. The backend verifies it
 * matches, so a token obtained from some cheaper form elsewhere can't be
 * replayed against the lead endpoint. Must stay in sync with
 * _RECAPTCHA_ACTION in juryview-api/src/leads/routes.py.
 */
const RECAPTCHA_ACTION = 'lead_submit';

export interface LeadPayload {
  name: string;
  email: string;
  message: string;  // Contains all form fields as formatted text

  // Anti-bot signals from useLeadFormGuard(). Optional so a caller that
  // doesn't use the hook still compiles — the server treats absent
  // signals as "unknown", not as "passed".
  website?: string;    // honeypot: non-empty means a bot filled it
  elapsedMs?: number;  // time from form mount to submit
}

export interface SubmitLeadResult {
  success: boolean;
  error?: string;
}

/**
 * Submit form data to your backend
 */
export async function submitLead(payload: LeadPayload): Promise<SubmitLeadResult> {

  // API endpoint configuration
  const ENDPOINT = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api/v1/leads/`
    : 'http://localhost:8000/api/v1/leads/';

  try {
    // Minted per submission — v3 tokens are single-use and expire after
    // two minutes, so this can't be hoisted to form mount. Resolves to
    // null rather than throwing when reCAPTCHA is blocked or
    // unconfigured; the server decides what a missing token means.
    const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);

    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: payload.name,
        email: payload.email,
        message: payload.message,
        recaptcha_token: recaptchaToken,
        website: payload.website ?? '',
        elapsed_ms: payload.elapsedMs,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: Submission failed`);
    }

    return { success: true };

  } catch (error) {
    console.error('[submitLead] Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit form'
    };
  }
}


/**
 * Helper: Pack form fields into a readable message string
 *
 * Use this when your backend only accepts { name, email, message }
 * This converts all form fields into a nicely formatted message.
 *
 * @example
 * const message = packFormFields({
 *   persona: 'Law Firm',
 *   needs: ['Focus Groups', 'Venue Modeling'],
 *   location: 'Houston, TX',
 *   company: 'Smith & Associates'
 * });
 *
 * // Result:
 * // "Provider Match Request
 * //
 * // Persona: Law Firm
 * // Needs: Focus Groups, Venue Modeling
 * // Location: Houston, TX
 * // Company: Smith & Associates"
 */
export function packFormFields(fields: Record<string, string | string[] | boolean>): string {
  const lines: string[] = ['Provider Match Request', ''];

  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null || value === '') continue;

    // Convert camelCase to Title Case (e.g., "requestType" → "Request Type")
    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');

    if (Array.isArray(value)) {
      lines.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'boolean') {
      lines.push(`${label}: ${value ? 'Yes' : 'No'}`);
    } else {
      lines.push(`${label}: ${value}`);
    }
  }

  return lines.join('\n');
}


/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                           EXPECTED PAYLOAD FORMAT                          ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  POST /api/v1/leads/                                                      ║
 * ║                                                                           ║
 * ║  {                                                                        ║
 * ║    "name": "John Smith",                                                  ║
 * ║    "email": "john@lawfirm.com",                                          ║
 * ║    "message": "Provider Match Request\n\nPersona: Law Firm\n...",        ║
 * ║    "recaptcha_token": "03AGdBq26...",   // v3 token, null if blocked     ║
 * ║    "website": "",                        // honeypot — must stay empty    ║
 * ║    "elapsed_ms": 18420                   // mount → submit, ms            ║
 * ║  }                                                                        ║
 * ║                                                                           ║
 * ║  The "message" field contains all form data as formatted text.           ║
 * ║                                                                           ║
 * ║  A rejected submission still returns 201 with success: true and no       ║
 * ║  lead_id. That is deliberate — telling a bot why it failed is free       ║
 * ║  tuning feedback. Genuine users never see this path.                     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */
